package com.essntl.features.file.ui.essntlgallery

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.file.domain.repository.FileRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import org.koin.compose.koinInject

@Composable
internal fun rememberEssntlGalleryViewModel(entity: EssntlGalleryState.Entity?): EssntlGalleryViewModel {
    val scope = rememberCoroutineScope()
    val fileRepository = koinInject<FileRepository>()
    val loadingManager = koinInject<LoadingManager>()

    return remember {
        EssntlGalleryViewModel(
            entity = entity,
            scope = scope,
            fileRepository = fileRepository,
            loadingManager = loadingManager,
        )
    }
}

class EssntlGalleryViewModel(
    private val entity: EssntlGalleryState.Entity?,
    private val fileRepository: FileRepository,
    val state: MutableStateFlow<EssntlGalleryState> = MutableStateFlow(EssntlGalleryState(galleryType = entity)),
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {
    fun onEvent(event: EssntlGalleryEvent) {
        when (event) {
            is EssntlGalleryEvent.GetAllImages -> getAllImages(event.entityType)
            is EssntlGalleryEvent.AddImages -> addImage(event)
            is EssntlGalleryEvent.SwitchTab -> switchTab(event)
            is EssntlGalleryEvent.DeleteImage -> deleteImage(event)
            is EssntlGalleryEvent.UpdateImage -> updateImage(event)
        }
    }

    private fun getAllImages(entityType: String) =
        getData(
            getData = { fileRepository.getByEntityType(entityType) },
            getState = { state.value.getAllImages },
            setState = { state.value = state.value.copy(getAllImages = it) },
        )

    private fun addImage(event: EssntlGalleryEvent.AddImages) =
        state.update {
            it.copy(
                getAllImages = it.getAllImages.copy(
                    data = it.getAllImages.data.orEmpty() + event.files,
                ),
            )
        }

    private fun switchTab(event: EssntlGalleryEvent.SwitchTab) {
        state.update {
            it.copy(
                selectedTabIndex = event.index,
                tabItems = if (event.dialog)
                    it.tabItems.map { tab -> if (tab.index != event.index) tab.copy(enabled = false) else tab } else
                    it.tabItems,
            )
        }

        if (entity != null) {
            onEvent(EssntlGalleryEvent.GetAllImages(entityType = entity.entityType()))
        } else {
            val defaultEntityType = EssntlGalleryState.Entity.entries[event.index].entityType()
            onEvent(EssntlGalleryEvent.GetAllImages(entityType = defaultEntityType))
        }
    }

    private fun deleteImage(event: EssntlGalleryEvent.DeleteImage) =
        state.update {
            it.copy(
                getAllImages = it.getAllImages.copy(
                    data = it.getAllImages.data
                        .orEmpty()
                        .filter { it.id != event.id },
                ),
            )
        }

    private fun updateImage(event: EssntlGalleryEvent.UpdateImage) =
        state.update {
            it.copy(
                getAllImages = it.getAllImages.copy(
                    data = it.getAllImages.data
                        .orEmpty()
                        .map {
                            if (it.id == event.file.id)
                                event.file else
                                it
                        },
                ),
            )
        }
}